import { createTheme } from '@mui/material/styles';

/**
 * Creates a dynamic theme based on the provided mode (light or dark).
 * @param {'light' | 'dark'} mode - The color mode.
 * @returns A MUI theme object.
 */
const createAppTheme = (mode = 'light') =>
   createTheme({
      palette: {
         mode,
         ...(mode === 'light'
            ? {
               // Light mode palette
               primary: { main: '#3B82F6', light: '#60A5FA', dark: '#2563EB' },
               secondary: { main: '#10B981', light: '#34D399', dark: '#059669' },
               success: { main: '#10B981', light: 'rgba(16, 185, 129, 0.1)' },
               error: { main: '#EF4444', light: 'rgba(239, 68, 68, 0.1)' },
               info: { main: '#3B82F6', light: 'rgba(59, 130, 246, 0.1)' },
               background: { default: '#F3F4F6', paper: '#FFFFFF' },
               text: { primary: '#1F2937', secondary: '#6B7280' },
            }
            : {
               // Dark mode palette
               primary: { main: '#60A5FA', light: '#3B82F6', dark: '#93C5FD' },
               secondary: { main: '#34D399', light: '#10B981', dark: '#6EE7B7' },
               success: { main: '#10B981', light: 'rgba(16, 185, 129, 0.2)' },
               error: { main: '#F87171', light: 'rgba(239, 68, 68, 0.2)' },
               info: { main: '#60A5FA', light: 'rgba(59, 130, 246, 0.2)' },
               background: { default: '#0B1220', paper: '#111827' },
               text: { primary: '#F9FAFB', secondary: '#D1D5DB' },
               divider: 'rgba(255, 255, 255, 0.12)',
            }), // <-- This parenthesis was fixed (removed one extra)
      },
      shape: { borderRadius: 8 },
      typography: {
         fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
         h4: {
            fontWeight: 600,
            fontSize: '2rem',
            '@media (max-width:600px)': { fontSize: '1.5rem' },
         },
         h5: {
            fontWeight: 600,
            fontSize: '1.5rem',
            '@media (max-width:600px)': { fontSize: '1.25rem' },
         },
         h6: {
            fontWeight: 600,
            fontSize: '1.25rem',
            '@media (max-width:600px)': { fontSize: '1.1rem' },
         },
      },
      components: {
         MuiButton: {
            styleOverrides: {
               root: {
                  textTransform: 'none',
                  borderRadius: 8,
                  '@media (max-width:600px)': {
                     minHeight: 44,
                     padding: '10px 16px',
                  },
               }
            }
         },
         MuiIconButton: {
            styleOverrides: {
               root: {
                  '@media (max-width:600px)': {
                     minWidth: 44,
                     minHeight: 44,
                  },
               },
            },
         },
         MuiCard: {
            styleOverrides: {
               root: {
                  borderRadius: 12,
                  // The 'mode' variable is available here from the function argument
                  boxShadow:
                     mode === 'light'
                        ? '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
                        : '0 4px 6px -1px rgb(255 255 255 / 0.03), 0 2px 4px -2px rgb(255 255 255 / 0.02)',
               },
            },
         },
         MuiContainer: {
            styleOverrides: {
               root: {
                  paddingLeft: 16,
                  paddingRight: 16,
                  '@media (min-width:600px)': { paddingLeft: 24, paddingRight: 24 },
                  '@media (min-width:960px)': { paddingLeft: 32, paddingRight: 32 },
               },
            },
         },
         MuiTextField: {
            styleOverrides: {
               root: {
                  '& .MuiInputBase-root': {
                     '@media (max-width:600px)': {
                        minHeight: 44,
                     },
                  },
               },
            },
         },
         MuiDrawer: {
            styleOverrides: {
               paper: { width: 240, boxSizing: 'border-box', overflow: 'hidden' },
            },
         },
      },
   });

export default createAppTheme;
